'use client';

import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { type GlycerinDisplayBasePayload } from '@packages/tracking/src/types/payloads';
import type { ReactNode } from 'react';
import React, { useState } from 'react';

export type TextWithDialogProps = {
  /** The text message you want to display, which can be clicked to open an dialog. */
  message: string;
  /**
   * The title for the dialog.
   */
  dialogTitle: string;
  /**
   * The content for the dialog.
   */
  dialogContent?: ReactNode;
  /**
   * This callback function is called when the dialog is opened through a click or keydown.
   */
  onOpenDialogHandler?: () => void;
  /**
   * This callback function is called after the dialog close button has been clicked.
   */
  onCloseHandler?: () => void;
  /**
   * This is props for displayOverlay tracking. it should be given to Dialog directly.
   */
  displayTrackingProps?: GlycerinDisplayBasePayload;
  /**
   * String of class names to be passed to the button.
   */
  className?: string;
} & Pick<React.HTMLProps<HTMLButtonElement>, 'aria-label'>;

/**
 * This component can be used to display a clickable text message. It opens an Dialog on click.
 * Additionaly you can pass all Typography props, like `sx` or `variant` to the clickable text message.
 * */
export const TextWithDialog = ({
  message,
  dialogTitle,
  dialogContent,
  onOpenDialogHandler,
  onCloseHandler,
  displayTrackingProps,
  className,
  'aria-label': ariaLabel,
}: TextWithDialogProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dispatchGTMEvent = useTracking();

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={() => {
          setIsDialogOpen(true);
          onOpenDialogHandler?.();
          if (displayTrackingProps) {
            dispatchDisplayOverlayTracking({
              eventDispatcher: dispatchGTMEvent,
              displayTrackingProps,
            });
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsDialogOpen(true);
            onOpenDialogHandler?.();
            if (displayTrackingProps) {
              dispatchDisplayOverlayTracking({
                eventDispatcher: dispatchGTMEvent,
                displayTrackingProps,
              });
            }
          }
        }}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        className={cn('cursor-pointer underline', className)}
      >
        {message}
      </span>
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          onCloseHandler?.();
        }}
        variant="bottomSheet"
      >
        <DialogHeader
          onClose={() => {
            setIsDialogOpen(false);
            onCloseHandler?.();
          }}
        >
          {dialogTitle}
        </DialogHeader>
        <DialogContent>{dialogContent}</DialogContent>
      </Dialog>
    </>
  );
};
